import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import {
  AlertCircle,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Clock,
} from 'lucide-react';

const formatStale = (hours) => {
  if (hours === null || hours === undefined) return 'Nunca';
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const rem = Math.round(hours - days * 24);
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
};

export function StaleSyncWidget({ data, loading }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div className="dashboard-card glass-widget rounded-lg p-5" data-testid="stale-sync-widget-loading">
        <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
          <Clock className="text-amber-400" size={20} />
          {t('dashboard.staleSync')}
        </h3>
        <div className="text-center py-8 text-muted-foreground animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const items = data?.items || [];
  const total = data?.total || 0;
  const displayItems = expanded ? items : items.slice(0, 5);

  return (
    <div className="dashboard-card glass-widget rounded-lg p-5 opacity-0 animate-scale-in stagger-4" data-testid="stale-sync-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <AlertCircle className="text-amber-400" size={18} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-foreground">{t('dashboard.staleSync')}</h3>
            <p className="text-xs text-muted-foreground">{t('dashboard.staleSyncDesc')}</p>
          </div>
        </div>
        {total > 0 && (
          <span className="text-xs bg-amber-500/20 text-amber-400 px-2.5 py-1 rounded-full font-medium" data-testid="stale-sync-count">
            {total} {total === 1 ? t('dashboard.staleSyncOne') : t('dashboard.staleSyncMany')}
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground" data-testid="stale-sync-empty">
          <CheckCircle size={32} className="mx-auto mb-2 text-emerald-400/50" />
          <p className="text-sm">{t('dashboard.staleSyncEmpty')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {displayItems.map((row, idx) => (
              <Link
                key={`${row.container_number}-${idx}`}
                to={`/shipments/${encodeURIComponent(row.shipment_mbl || '')}`}
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
                data-testid={`stale-row-${row.container_number}`}
              >
                <div className="min-w-0 flex-1 mr-3">
                  <p className="text-sm font-medium text-foreground truncate font-mono">{row.container_number}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs text-muted-foreground font-mono">{row.shipment_mbl}</span>
                    {row.client_name && (
                      <span className="text-xs text-muted-foreground truncate">· {row.client_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-amber-400/80 mt-0.5">
                    {t(`statusCodes.${row.status_container_code}`) || row.status_container_code}
                  </div>
                </div>
                <span className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  row.hours_stale === null
                    ? 'bg-red-500/20 text-red-400'
                    : row.hours_stale > 72
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {formatStale(row.hours_stale)}
                </span>
              </Link>
            ))}
          </div>
          {items.length > 5 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-3 py-2 text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1 transition-colors"
              data-testid="stale-sync-toggle"
            >
              {expanded ? (
                <>{t('dashboard.viewLess')} <ChevronUp size={14} /></>
              ) : (
                <>{t('dashboard.viewMore')} {items.length - 5} {t('dashboard.more')} <ChevronDown size={14} /></>
              )}
            </button>
          )}
          <Link to="/sync-health" className="block mt-3">
            <button className="w-full py-2 text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1 transition-colors border-t border-border/50 pt-3" data-testid="stale-sync-view-all">
              {t('dashboard.staleSyncViewHealth')} <ArrowRight size={14} />
            </button>
          </Link>
        </>
      )}
    </div>
  );
}
